import { ReactNode, Suspense, useEffect } from 'react';

import { StorageManager } from '@speeki/storage-manager';

import { TenantModulesByUuidDocument } from '@graphql/generated/graphql';

import { checkIfTeams } from '@utils/helpers';
import { useCompanyCodeRedirect } from '@utils/hooks/useCompanyCodeRedirect';
import { useCustomUrl } from '@utils/hooks/useCustomUrl';
import { useMsTeamsRedirect } from '@utils/hooks/useMsTeamsRedirect';
import { useNotifications } from '@utils/hooks/useNotifications';
import { useRedirectByCompanySlug } from '@utils/hooks/useRedirectByCompanySlug';

import { getGraphApi } from '@services/ApiService';
import { AuthService } from '@services/AuthService/AuthService';
import { sentry } from '@services/Sentry';

import { useQuery } from '@apollo/client';
import { tenantModulesVar } from '@boot/reactiveVariables';
import { useChatRootSetup } from '@domains/Chat/useChatRootSetup';
import { SkeletonDashboardPage } from '@domains/dashboard/DashboardPage.skeleton';
import { app } from '@microsoft/teams-js';
import { REACT_APP_APP_DOMAIN } from '@shared/constants';

/**
 * NOTICE: if page have own redirect logic use clean react-router <Route />
 */
interface AppLateInitProps {
    children: ReactNode;
}

export const AppLateInit = ({ children }: AppLateInitProps) => {
    useNotifications();
    useChatRootSetup();

    const tenantUuid = AuthService.getTenantUuid();

    const { teamsLoading } = useMsTeamsRedirect();
    const { companyCodeLoading } = useCompanyCodeRedirect();
    const { customUrlLoading } = useCustomUrl();
    const { redirectLoading } = useRedirectByCompanySlug();
    const { loading: tenantLoading } = useQuery(TenantModulesByUuidDocument, {
        onCompleted: ({ tenant }) => {
            tenantModulesVar(tenant.modules);
        },
        onError: (error) => {
            console.error('AppLateInit TenantModulesByUuid :: ', error);
            StorageManager.removeValue('tenantUuid');
            window.location.href = `${window.location.protocol}//${REACT_APP_APP_DOMAIN}`;
        },
        skip: !tenantUuid || !getGraphApi(),

        variables: {
            uuid: tenantUuid ?? '',
        },
    });

    const loading =
        customUrlLoading ||
        redirectLoading ||
        companyCodeLoading ||
        teamsLoading ||
        tenantLoading;

    useEffect(() => {
        sentry.setUser();

        checkIfTeams() && app.notifySuccess();
    }, []);

    return (
        <Suspense fallback={<SkeletonDashboardPage />}>
            {loading ? <SkeletonDashboardPage /> : children}
        </Suspense>
    );
};
